// react
import { useContext } from 'react';

// types
import { Category, CategoryStatus } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Chip, Typography } from '@mui/material';

// ui
import { BtnMenu } from '../../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { MenuItemProps } from '../../../ui/Menu/Menu';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';

// dialogs
import { DialogEditCategory } from '../../../dialogs/DialogEditCategory/DialogEditCategory';
import { DialogUpdateCategoryStatus } from '../../../dialogs/DialogUpdateCategoryStatus/DialogUpdateCategoryStatus';

// tables
import { TableBasicAllocations } from '../TableBasicAllocations/TableBasicAllocations';

// icons
import {
	DoDisturbOutlined as IconDoDisturb,
	ModeEditOutlined as IconModeEdit,
	PowerSettingsNew as IconPowerSettingsNew,
} from '@mui/icons-material';

// theme
import { theme } from '../../../../theme';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TableBasicCategoriesProps extends Omit<TableProps, 'headers'> {
	data: Category[];
	refetchCategories?: () => void;
}

export const TableBasicCategories = ({
	data = [],
	refetchCategories,
	...props
}: TableBasicCategoriesProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');

	const headers: TableHeader[] = [
		{
			label: t('name'),
			render: (name: string) => {
				return <Typography fontWeight={600}>{name}</Typography>;
			},
			value: 'name',
		},
	];

	if (featureFlags['CONTRACT_WORKFLOW']) {
		headers.push({
			label: t('id'),
			render: (idWbg: string | undefined) => {
				return <Typography>{idWbg || t('na')}</Typography>;
			},
			value: 'idWbg',
		});
	}

	headers.push(
		{
			label: t('type'),
			render: (isDefault: boolean) => {
				return (
					<Chip
						color={isDefault ? 'info' : 'purple'}
						label={isDefault ? 'World Bank' : t('project')}
						size="small"
						variant="twoTone"
					/>
				);
			},
			value: 'isDefault',
		},
		{
			label: t('status'),
			render: (status: CategoryStatus) => {
				return <ChipStatus status={status} />;
			},
			value: 'status',
		},
		{
			label: t('created'),
			render: (created: string) => {
				return <Time date={created} />;
			},
			value: 'created',
		},
		{
			align: 'right',
			label: '',
			render: (name: string, header: TableHeader, component: Category) => {
				const btnMenuItems = [];

				if (userProjectPermissions?.category?.update) {
					btnMenuItems.push({
						children: t('editCategory'),
						icon: <IconModeEdit />,
						onClick: () => {
							searchParams.set('dialog', 'editCategory');
							searchParams.set('dialogCategory', String(component.id));
							setSearchParams(searchParams);
						},
					});

					const btnUpdateStatus: MenuItemProps = {
						children: t('deactivateCategory'),
						icon: <IconDoDisturb color="error" />,
						onClick: () => {
							searchParams.set('dialog', 'updateCategoryStatus');
							searchParams.set('dialogCategory', String(component.id));
							searchParams.set('dialogStatus', 'DEACTIVATED');
							setSearchParams(searchParams);
						},
					};

					if (component.status === 'DEACTIVATED') {
						btnUpdateStatus.children = t('activateCategory');
						btnUpdateStatus.icon = <IconPowerSettingsNew color="success" />;
						btnUpdateStatus.onClick = () => {
							searchParams.set('dialog', 'updateCategoryStatus');
							searchParams.set('dialogCategory', String(component.id));
							searchParams.set('dialogStatus', 'ACTIVE');
							setSearchParams(searchParams);
						};
					}

					btnMenuItems.push(btnUpdateStatus);
				}

				if (btnMenuItems.length) {
					return <BtnMenu items={btnMenuItems} />;
				}

				return <></>;
			},
			value: 'created',
		}
	);

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogCategory');
		searchParams.delete('dialogStatus');
		setSearchParams(searchParams);
	};

	const dataEnhanced = data.map((item, i) => {
		const isLast = i === data.length - 1;

		let expandedContent;

		if (featureFlags['IFRS']) {
			expandedContent = (
				<TableBasicAllocations
					data={item.allocations || []}
					sxTable={{
						borderBottomColor: theme.palette.divider,
						borderBottomStyle: 'solid',
						borderBottomWidth: isLast ? 0 : 1,
					}}
				/>
			);
		}

		return {
			...item,
			expandedContent,
		};
	});

	return (
		<>
			<Table {...props} data={dataEnhanced} headers={headers} />
			<DialogEditCategory
				isOpen={paramDialog === 'editCategory'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchCategories) {
						refetchCategories();
					}
				}}
			/>
			<DialogUpdateCategoryStatus
				isOpen={paramDialog === 'updateCategoryStatus'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchCategories) {
						refetchCategories();
					}
				}}
			/>
		</>
	);
};
